import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import { useToast } from '@iheartjane/reefer';
import { Form, useForm } from '@iheartjane/reefer-hook-form';
import type { InfiniteData } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import type {
  ColumnFiltersState,
  RowSelectionState,
  SortingState,
} from '@tanstack/react-table';
import {
  getCoreRowModel,
  getSortedRowModel,
  useLegacyTable,
} from '@tanstack/react-table/legacy';
import type { MouseEvent as ReactMouseEvent } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import {
  fetchUnpublishedProductQueryKey,
  useFetchUnpublishedProducts,
  useUpdateUnpublishedProduct,
} from '@jane/business-admin/data-access';
import { useHasPermissions, useStoreId } from '@jane/business-admin/hooks';
import { useProductsTable } from '@jane/business-admin/providers/ProductsTableProvider';
import {
  EventNames,
  ModalNames,
  normalizePath,
  track,
} from '@jane/business-admin/util';
import { Permission } from '@jane/shared/auth';

import { BaseProductTable } from './BaseProductTable';
import { EmptyState } from './EmptyStates';
import { filterParams, sortParams } from './PublishedProductTable';
import { buildUnpublishedColumns } from './UnpublishedColumns';

export const UnpublishedProductTable = ({
  bulkEditModalOpen,
  columnFiltersParent,
  isBulkEditTable = false,
  selectedProducts,
  setColumnFiltersParent,
  setFullSelectedProducts,
  setSelectedProducts,
}: {
  bulkEditModalOpen: boolean;
  columnFiltersParent: ColumnFiltersState;
  isBulkEditTable?: boolean;
  selectedProducts: RowSelectionState;
  setColumnFiltersParent?: any;
  setFullSelectedProducts: any;
  setSelectedProducts: any;
}) => {
  const navigate = useNavigate();
  const userCanEditUnpublishedProducts = useHasPermissions([
    Permission.EditUnpublishedProducts,
  ]);
  const { setCanEditProducts } = useProductsTable();

  useEffect(() => {
    setCanEditProducts(userCanEditUnpublishedProducts ?? false);
  }, [userCanEditUnpublishedProducts]);

  const formMethods = useForm();
  const storeId = useStoreId();
  const [sorting, setSorting] = useState<SortingState>([]);
  const toast = useToast();
  const showClarification = useFlag(FLAGS.c3AliasClarification);
  const bulkEditInvalidProducts = useFlag(FLAGS.c1BulkEditInvalidProducts);

  const {
    mutateAsync: publishProduct,
    isPending: isPublishing,
    isSuccess: isUpdateSuccess,
    isError: isUpdateError,
  } = useUpdateUnpublishedProduct(storeId);

  const { pathname } = useLocation();
  const openUnpublishedModal = (row: any) => {
    track({
      event: EventNames.OpenedModal,
      modal_name: ModalNames.UpdateMenuProduct,
      url: normalizePath(pathname, storeId),
    });
    navigate(`${row.unmapped_product_id}`);
  };

  const {
    data,
    isFetched,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useFetchUnpublishedProducts({
    params: {
      ...filterParams(columnFiltersParent),
      ...sortParams(sorting),
    },
    storeId,
  });

  const totalRowCount = useMemo(
    () => data?.pages?.[0]?.meta?.total ?? 0,
    [JSON.stringify(data)]
  );

  const columns = useMemo(
    () =>
      buildUnpublishedColumns({
        bulkEditInvalidProducts,
        isBulkEditTable,
        isFetched,
        openUnpublishedModal,
        publishProduct,
        isPublishing,
        showClarification,
        totalRowCount,
        sorting,
      }),
    [
      JSON.stringify(data),
      bulkEditInvalidProducts,
      isBulkEditTable,
      isFetched,
      openUnpublishedModal,
      publishProduct,
      isPublishing,
      showClarification,
      totalRowCount,
      sorting,
    ]
  );

  const allRows = useMemo(
    () =>
      data?.pages
        ?.flatMap((page) => page.meta.all_product_ids)
        .reduce((a, v) => ({ ...a, [v]: true }), {}) ?? {},
    [JSON.stringify(data)]
  );

  const productData = useMemo(
    () => data?.pages?.flatMap((page) => page.unpublished_products) ?? [],
    [JSON.stringify(data)]
  );

  const queryClient = useQueryClient();
  const totalRowsFromCache =
    queryClient.getQueryData<InfiniteData<any>>(
      fetchUnpublishedProductQueryKey(storeId, {})
    )?.pages?.[0]?.meta?.total ?? 0;

  const reactTable = useLegacyTable({
    data: productData,
    columns,
    initialState: {
      columnVisibility: { unmapped_product_id: true },
    },
    state: {
      columnFilters: columnFiltersParent,
      sorting,
      rowSelection: selectedProducts,
    },
    onColumnFiltersChange: setColumnFiltersParent,
    onSortingChange: setSorting,
    onRowSelectionChange: setSelectedProducts,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableMultiRowSelection: true,
    enableRowSelection: true,
    getRowId: (row) => row.unmapped_product_id.toString(),
    manualSorting: true,
    manualFiltering: true,
    manualPagination: true,
  });

  useEffect(() => {
    if (!bulkEditModalOpen) {
      setColumnFiltersParent([]);
    }
  }, [storeId, bulkEditModalOpen]);

  // persist filtering when bulk edit modal is opened
  useEffect(() => {
    if (isBulkEditTable) {
      setColumnFiltersParent([
        ...columnFiltersParent,
        {
          id: 'status',
          value: bulkEditInvalidProducts
            ? ['unpublished', 'invalid_data']
            : ['unpublished'],
        },
      ]);
    }
  }, [isBulkEditTable]);

  const handleRowClick = (
    row: any,
    e: ReactMouseEvent<HTMLTableRowElement, MouseEvent>
  ) => {
    if (
      row.status === 'publishing' ||
      isBulkEditTable ||
      !userCanEditUnpublishedProducts
    ) {
      e.preventDefault();
      return;
    }

    openUnpublishedModal(row);
  };

  useEffect(() => {
    if (isPublishing) {
      toast.add({ label: 'Publishing...', variant: 'success' });
    }
    if (isUpdateSuccess) {
      toast.add({
        label: 'Product successfully published!',
        variant: 'success',
      });
    }
    if (isUpdateError) {
      toast.add({ label: 'Product could not be published', variant: 'error' });
    }
  }, [isPublishing, isUpdateSuccess, isUpdateError]);

  useEffect(() => {
    const formattedProducts = reactTable
      .getSelectedRowModel()
      .rows.map((row) => ({
        name: row.original.pos_data.name,
        kind: row.original.pos_data.kind,
        unmapped_product_id: row.original.unmapped_product_id,
        category: row.original.pos_data.category,
        brand: row.original.pos_data.brand,
        // Invalid products may lack lab_results; guard so selecting them doesn't throw.
        percent_cbd: row.original.pos_data.lab_results?.percent_cbd,
        percent_thc: row.original.pos_data.lab_results?.percent_thc,
        cbd_dosage_milligrams: 0,
        thc_dosage_milligrams: 0,
        description: row.original.pos_data.description,
        image_urls: row.original.pos_data.image_urls,
        status: row.original.status,
      }));

    setFullSelectedProducts({ products: formattedProducts });
  }, [selectedProducts]);

  return isFetched && totalRowsFromCache === 0 ? (
    <EmptyState variant="unpublished" />
  ) : (
    <Form.BaseForm formMethods={formMethods} onSubmit={() => null} name="test">
      <BaseProductTable
        allIds={allRows}
        table={reactTable}
        productData={productData}
        fetchNextPage={fetchNextPage}
        isBulkEditTable={isBulkEditTable}
        isFetched={isFetched}
        isFetching={isFetching && !isFetchingNextPage}
        handleRowClick={handleRowClick}
        hasNextPage={hasNextPage || false}
        tableType="unpublished"
      />
    </Form.BaseForm>
  );
};
